import type { AgentContext } from '@backrs_ui/grow-ui/src/agentContext/types';
import {
  Artifact,
  StreamedMessageResponse,
  DeltaStreamCallbacks,
} from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import { ChartSpec, ResourcesContent, SessionStatus } from '@backrs_ui/grow-ui/src/models/models';
import {
  createDeltaStreamHandler,
  createTextStreamHandler,
} from '@backrs_ui/grow-ui/src/utils/streamUtils';

import { createApi } from '@reduxjs/toolkit/query/react';

import { lubavAppBaseQuery } from '../baseQuery';

type UpdateResponseFeedbackRequest = Readonly<{
  response_id: number;
  liked_response?: boolean;
  user_feedback?: string;
}>;

type UpdateResponseFeedbackResponse = Readonly<{
  success: boolean;
}>;

type AttachedNuggetPayload = Readonly<{
  type: string;
  id: string;
  title?: string;
}>;

export type AttachedFilePayload = Readonly<{
  // A file attachment has an s3_key; a link attachment has a source_url instead.
  s3_key?: string;
  filename: string;
  mime_type: string;
  source_url?: string;
}>;

type SendMessageRequest = Readonly<{
  text: string;
  session_id: number;
  user_id?: number;
  llm: string;
  language: string;
  is_guidance_counselor: boolean;
  as_supercoach: boolean;
  coaching_user_id?: number;
  include_profile_items?: string[];
  office_hours_session_type?: string;
  attached_nuggets?: readonly AttachedNuggetPayload[];
  attached_files?: readonly AttachedFilePayload[];
  agent_context?: AgentContext;
  signal?: AbortSignal;
  prompt_button_id?: string;
  display_text?: string;
  required_documents?: string[];
  allow_skip_document?: boolean;
  confirmed_documents?: string[];
}> &
  DeltaStreamCallbacks;

type GetInitialMessageQueryParams = Readonly<{
  session_id: number;
  signal?: AbortSignal;
}> &
  DeltaStreamCallbacks;

type GetNewSessionIdForMessageEditRequest = Readonly<{
  session_id: number;
  query_id: string;
  create_unlinked_session?: boolean;
}>;

type GetNewSessionIdForMessageEditResponse = Readonly<{
  session_id: number;
}>;

type InitiateSessionRequest = Readonly<{
  session_type: string;
  coaching_user_id?: number;
}>;

type InitiateSessionResponse = number;

type GetAttachmentUrlRequest = Readonly<{
  s3_key: string;
}>;

type GetAttachmentUrlResponse = Readonly<{
  url: string;
}>;

type IngestLinkRequest = Readonly<{
  url: string;
  classify_as?: string[];
}>;

export type IngestLinkResponse = Readonly<{
  viewable: boolean;
  source_kind: 'native_gdoc' | 'google_drive_binary' | 'web' | 'onedrive' | 'canva';
  refresh_policy: 'live' | 'once';
  reason: 'private' | 'unreadable' | 'unsupported_host' | null;
  unsupported_host: 'onedrive' | 'canva' | null;
  media_id?: number;
  media_type?: 'pdf' | 'txt' | 'docx' | 'image';
  source_url?: string;
  content_type?: string;
  filename?: string;
  document_type?: 'resume' | 'transcript' | 'personal_assessment' | 'other' | null;
  text?: string | null;
}>;

type RemoveSessionResourcesRequest = Readonly<{
  session_id: number;
  resource_keys: string[];
  only_non_featured?: boolean;
}>;

type RemoveSessionResourcesResponse = Readonly<{
  success: boolean;
  deleted_count: number;
}>;

type GetOfficeHoursSessionDataRequest = Readonly<{
  session_id: number;
}>;

type AppendMessageRequest = Readonly<{
  session_id: number;
  role: 'user' | 'ai';
  text: string;
}>;

type AppendMessageResponse = Readonly<{
  message_id: number;
}>;

export type { SessionStatus };

type GetSessionStatusRequest = Readonly<{
  structured_session_type: string;
}>;

type GetSessionStatusResponse = Readonly<{
  status: SessionStatus;
  session_id: number | null;
}>;

type GetSessionStatusesResponse = Readonly<{
  statuses: Record<string, GetSessionStatusResponse>;
}>;

// coaching_user_id reads a student's statuses instead of the caller's own
type GetSessionStatusesRequest = Readonly<{ coaching_user_id: number }> | undefined;

type StreamProfileSummaryRequest = Readonly<{
  user_id: number;
  onChunk?: (chunk: string) => void;
}>;

export type LubavSession = Readonly<{
  id: number;
  type: string;
  session_title: string;
  created_at: string;
  modified_at: string;
  coaching_user_id?: number | null;
}>;

type GetAllSessionsRequest = Readonly<{
  limit?: number;
  offset?: number;
}>;

type GetAllSessionsResponse = Readonly<{
  sessions: LubavSession[];
  total_count: number;
  has_more: boolean;
}>;

export type GetOfficeHoursSessionDataResponse = Readonly<{
  session_id: number;
  user_id?: number;
  session_title: string;
  session_type: string;
  messages: Readonly<{
    id: number;
    sender: 'ai' | 'human';
    text: string;
    question?: string;
    type?: string;
    options?: string[];
    documents_requested?: string;
    response_id?: number;
    field?: string;
    display_name?: string;
    include_freeform?: boolean;
    charts?: ChartSpec[];
  }>[];
  additional_resources: Partial<ResourcesContent>;
  finished: boolean;
  subsession_type?: string;
  artifact?: Artifact;
  featured_nugget_types?: string[];
}>;

export const officeHoursApi = createApi({
  reducerPath: 'officeHoursApi',
  baseQuery: lubavAppBaseQuery,
  tagTypes: ['SessionsList', 'SessionData'],
  endpoints: (build) => ({
    updateResponseFeedback: build.mutation<
      UpdateResponseFeedbackResponse,
      UpdateResponseFeedbackRequest
    >({
      query: (body) => ({
        url: '/api/office-hours/update-response-feedback',
        method: 'POST',
        body,
      }),
    }),
    removeSessionResources: build.mutation<
      RemoveSessionResourcesResponse,
      RemoveSessionResourcesRequest
    >({
      query: (body) => ({
        url: '/api/office-hours/remove-session-resources',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { session_id }) => [
        { type: 'SessionData', id: session_id },
      ],
    }),
    sendMessage: build.mutation<StreamedMessageResponse, SendMessageRequest>({
      query: ({
        onMeta,
        onDelta,
        onArtifact,
        onChart,
        onTool,
        onNugget,
        onFeaturedType,
        onComponent,
        onDone,
        signal,
        ...body
      }) => ({
        url: '/api/office-hours/send-message',
        method: 'POST',
        body,
        signal,
        responseHandler: createDeltaStreamHandler({
          onMeta,
          onDelta,
          onArtifact,
          onChart,
          onTool,
          onNugget,
          onFeaturedType,
          onComponent,
          onDone,
        }),
      }),
    }),
    getInitialMessage: build.mutation<StreamedMessageResponse, GetInitialMessageQueryParams>({
      query: ({ onMeta, onDelta, onChart, onTool, onComponent, onDone, signal, ...body }) => ({
        url: '/api/office-hours/get-initial-message',
        method: 'POST',
        body,
        signal,
        responseHandler: createDeltaStreamHandler({
          onMeta,
          onDelta,
          onChart,
          onTool,
          onComponent,
          onDone,
        }),
      }),
    }),
    getNewSessionIdForMessageEdit: build.mutation<
      GetNewSessionIdForMessageEditResponse,
      GetNewSessionIdForMessageEditRequest
    >({
      query: (body) => ({
        url: '/api/office-hours/get-new-session-id-for-message-edit',
        method: 'POST',
        body,
      }),
    }),
    initiateSession: build.mutation<InitiateSessionResponse, InitiateSessionRequest>({
      query: (body) => ({
        url: '/api/initiate-session',
        method: 'POST',
        body,
      }),
    }),
    getAttachmentUrl: build.mutation<GetAttachmentUrlResponse, GetAttachmentUrlRequest>({
      query: (body) => ({
        url: '/api/office-hours/get-attachment-url',
        method: 'POST',
        body,
      }),
    }),
    ingestLink: build.mutation<IngestLinkResponse, IngestLinkRequest>({
      query: (body) => ({
        url: '/api/ingest-link',
        method: 'POST',
        body,
      }),
    }),
    getOfficeHoursSessionData: build.query<
      GetOfficeHoursSessionDataResponse,
      GetOfficeHoursSessionDataRequest
    >({
      query: ({ session_id }) => ({
        url: '/api/office-hours/get-session-data',
        method: 'POST',
        body: session_id,
      }),
      providesTags: (_result, _error, { session_id }) => [{ type: 'SessionData', id: session_id }],
    }),
    getAllSessions: build.query<GetAllSessionsResponse, GetAllSessionsRequest>({
      query: ({ limit = 10, offset = 0 }) => ({
        url: '/api/office-hours/get-all-sessions',
        method: 'POST',
        body: { limit, offset },
      }),
      providesTags: ['SessionsList'],
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.offset === 0) {
          return newItems;
        }

        return {
          sessions: [...currentCache.sessions, ...newItems.sessions],
          total_count: newItems.total_count,
          has_more: newItems.has_more,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.offset !== previousArg?.offset;
      },
    }),
    appendMessage: build.mutation<AppendMessageResponse, AppendMessageRequest>({
      query: (body) => ({
        url: '/api/office-hours/append-message',
        method: 'POST',
        body,
      }),
    }),
    getSessionStatus: build.query<GetSessionStatusResponse, GetSessionStatusRequest>({
      query: ({ structured_session_type }) => ({
        url: '/api/office-hours/get-session-status',
        method: 'POST',
        body: { structured_session_type },
      }),
    }),
    getSessionStatuses: build.query<GetSessionStatusesResponse, GetSessionStatusesRequest>({
      query: (args) => ({
        url: '/api/office-hours/get-session-statuses',
        method: 'POST',
        body: args,
      }),
    }),
    streamProfileSummary: build.mutation<string, StreamProfileSummaryRequest>({
      queryFn: async ({ onChunk, ...body }, _queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/api/stream-profile-summary',
          method: 'POST',
          body,
          responseHandler: createTextStreamHandler(onChunk),
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as string };
      },
    }),
  }),
});

export const {
  useUpdateResponseFeedbackMutation,
  useRemoveSessionResourcesMutation,
  useSendMessageMutation,
  useGetInitialMessageMutation,
  useGetNewSessionIdForMessageEditMutation,
  useInitiateSessionMutation,
  useGetAttachmentUrlMutation,
  useIngestLinkMutation,
  useGetOfficeHoursSessionDataQuery,
  useGetAllSessionsQuery,
  useAppendMessageMutation,
  useGetSessionStatusQuery,
  useGetSessionStatusesQuery,
  useStreamProfileSummaryMutation,
} = officeHoursApi;
